/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import axios, { AxiosError } from 'axios'
import { toast } from 'react-toastify'
import { makeStyles, Theme } from '@material-ui/core/styles'
import {
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  Typography,
  Box,
  CircularProgress,
  Select,
  MenuItem
} from '@material-ui/core'
import { CloudDownload, Visibility, PostAdd, NotInterested } from '@material-ui/icons'
import { ServerError } from '../../../schemas/Error'
import UserContext from '../../../contexts/User'
import { UserContextType } from '../../../schemas/User'
import PageContainer from '../Common/PageContainer'
import ResponsiveList, { ResponsiveColumn } from '../Common/ResponsiveList'
import DocumentStateBadge from '../Common/DocumentStateBadge'
import { urls } from '../../../urls'
import ElectronicBillService from '../../../services/ElectronicBill'
import Utils from '../../../utils'
import permissions from '../../../permissions.json'
import PresentCreditNoteModal from './PresentCreditNoteModal'

// -------------- Styles --------------
const useStyles = makeStyles((theme: Theme) => ({
  alignRight: {
    textAlign: 'right',
  },
  limitSelect: {
    minWidth: 200,
    [theme.breakpoints.down('xs')]: {
      width: '100%'
    }
  },
  loadingContainer: {
    position: 'fixed',
    width: '100%',
    height: '100%',
    bottom: '0',
    right: '0',
    background: '#BFBFBFBF',
    opacity: 0.6,
    display: 'flex',
    justifyContent: 'center',
    paddingTop: '50vh'
  }
}))

// - in this part all the component texts are housed
const texts = {
  header: {
    description: 'Lista de facturas emitidas'
  }
}

const limitOptions = [100, 200, 500]

// widthPct: porcentaje del ancho de la tabla (las columnas visibles siempre suman 100%)
const columns: ResponsiveColumn[] = [
  {
    field: 'number',
    headerName: 'Factura',
    widthPct: 9,
    editable: false,
  },
  {
    field: 'date',
    headerName: 'Fecha',
    widthPct: 18,
    editable: false,
  },
  {
    field: 'third',
    headerName: 'Nombre',
    editable: false,
    widthPct: 20,
    cardTitle: true,
  },
  {
    field: 'note',
    headerName: 'Descripción',
    editable: false,
    widthPct: 17,
    hideOnTablet: true,
  },
  {
    field: 'state',
    headerName: 'Estado',
    editable: false,
    widthPct: 10,
    renderCell: (params: any) => (
      <DocumentStateBadge state={params.row.state} />
    ),
    renderCard: (row: any) => <DocumentStateBadge state={row.state} />,
  },
  {
    field: 'actions',
    headerName: 'Acciones',
    widthPct: 26,
    minWidth: 180,
    type: 'actions',
    renderCell: (params: any) => (
      <ActionButtons params={params} />
    )
  }
]

// ------------ Init state -----------
interface State {
  electronicBills: any[]
  loading: boolean
  docsLength: number
  totalDocuments: number
}

const initState: State = {
  electronicBills: [],
  loading: true,
  docsLength: 0,
  totalDocuments: 0
}

function ActionButtons(props: any) {
  const navigate = useNavigate()
  const { userContext } = React.useContext(UserContext) as UserContextType
  const [showConfirmCreditNoteModal, setShowConfirmCreditNoteModal] = useState(false)
  const [invoiceNumberForCreditNote, setInvoiceNumberForCreditNote] = useState<number | null>(null)

  const handleConfirmCreditNoteModal = (show: boolean) => {
    setShowConfirmCreditNoteModal(show)
    if (!show) setInvoiceNumberForCreditNote(null)
  }

  const onView = () => {
    navigate(`/${urls.app.index}/${urls.app.main.electronicBill.form}?number=${props.params.row.number ?? ''}`)
  }

  const onCopy = () => {
    navigate(`/${urls.app.index}/${urls.app.main.electronicBill.form}?number=${props.params.row.number ?? ''}&copy=true`)
  }

  const onDownload = async () => {
    if (props.params.row.number) {
      try {
        const electronicBillService = new ElectronicBillService()
        const htmlContent = await electronicBillService.getElectronicInvoiceHTML(props.params.row.number)
        
        // Generar el PDF con el HTML obtenido
        const invoiceNumber = props.params.row.number.toString()
        Utils.generateElectronicInvoicePDF(htmlContent, invoiceNumber)
        
        // Mostrar mensaje de éxito
        toast.success('PDF de factura generado correctamente')
      } catch (error) {
        console.error('Error generando PDF de factura:', error)
        toast.error(`Error generando PDF de factura: ${error}`)
      }
    } else {
      toast.error('No hay número de factura disponible')
    }
  }

  const onOpenPresentCreditNoteModal = () => {
    if (props.params.row.number != null) {
      setInvoiceNumberForCreditNote(props.params.row.number)
      setShowConfirmCreditNoteModal(true)
    } else {
      toast.error('No hay número de factura disponible para presentar nota crédito')
    }
  }

  return (
    <React.Fragment>
      <IconButton
        type="submit"
        title="Ver factura"
        onClick={() => onView()}
      >
        <Visibility fontSize={"small"} color="primary" />
      </IconButton>

      <IconButton
        type="submit"
        title="Copiar factura"
        onClick={() => onCopy()}
      >
        <PostAdd fontSize={"small"} color="action" />
      </IconButton>

      <IconButton
        type="submit"
        title="Descargar PDF de la factura"
        onClick={() => onDownload()}
      >
        <CloudDownload fontSize={"small"} color="primary" />
      </IconButton>

      {userContext.permissions.some(permission => permission === permissions.electronic_bill.present_credit_note) && (
        <IconButton
          type="button"
          title="Presentar nota crédito"
          onClick={onOpenPresentCreditNoteModal}
        >
          <NotInterested fontSize="small" color="secondary" />
        </IconButton>
      )}
      <PresentCreditNoteModal
        show={showConfirmCreditNoteModal}
        invoiceNumber={invoiceNumberForCreditNote}
        handleModal={handleConfirmCreditNoteModal}
      />
    </React.Fragment>
  )
}

export default function ElectronicBillList() {

  const classes = useStyles()

  const [state, setState] = useState<State>(initState)
  const [limit, setLimit] = useState<number>(200)

  const handleLimitChange = (event: React.ChangeEvent<{ name?: string; value: unknown }>) => {
    setLimit(Number(event.target.value))
  }

  React.useEffect(() => {
    async function loadData() {
      try {
        setState({
          ...state,
          loading: true
        })
        const electronicBillService= new ElectronicBillService()
        const response = await electronicBillService.getElectronicBillsFromPlemsi(limit)
        let bills = [];
        let docsLength = 0
        let totalDocuments = 0

        if (response.totalDocuments > 0) {
          docsLength = response.docs.length
          totalDocuments = response.totalDocuments
          bills = response.docs.map((bill: any) => {
            return {
              number: bill.number,
              date: `${bill.date} ${bill.time || ''}`.trim(),
              third: bill.customer.name,
              note: bill.order_reference?.id_order || '-',
              preview: bill.preview,
              cude: bill.cude,
              state: bill.state
            }
          })
        }
        setState({
          ...state,
          electronicBills: bills,
          loading: false,
          docsLength,
          totalDocuments
        })
      } catch (error) {
        setState({
          ...state,
          loading: false
        })
        if (axios.isAxiosError(error)) {
          const serverError = error as AxiosError<ServerError>
          if (serverError && serverError.response) {
            return toast.error(serverError.response.data.message)
          }
        }
      }
    }
    loadData()
  }, [limit])


  return (
    <PageContainer title={texts.header.description}>
      <Grid container spacing={3}>

        <Grid item xs={12}>
          <Typography variant="body2">
            Mostrando {state.docsLength} de {state.totalDocuments}
          </Typography>
        </Grid>

        {/* ----- Listado: tabla (>= 600px) o cards (mobile) ------*/}
        <Grid item xs={12}>
          <ResponsiveList
            rows={state.electronicBills}
            columns={columns}
            keyId='number'
            cardActions={(row) => <ActionButtons params={{ row }} />}
          />
        </Grid>

        <Grid item xs={12} className={classes.alignRight}>
          <FormControl variant="outlined" className={classes.limitSelect}>
            <InputLabel id="limit-label">Cantidad a consultar</InputLabel>
            <Select
              labelId="limit-label"
              label="Cantidad a consultar"
              value={limit}
              onChange={handleLimitChange}
            >
              {
                limitOptions.map((limitOption) => (
                  <MenuItem key={limitOption} value={limitOption}>
                    {limitOption}
                  </MenuItem>
                ))
              }
            </Select>
          </FormControl>
        </Grid>
      </Grid>
      {
        state.loading ?
        (
          <div className={classes.loadingContainer}>
            <Box>
              <CircularProgress/>
            </Box>
          </div>
        ) : ''
      }
    </PageContainer>
  )
}
